<template>
  <Header
    navbarType="initial"
    v-if="currentStep !== RegistrationSteps.LOADING"
    :application-id="creditLineApplicationId"
    class="mb-0 pb-0"
  />
  <Alert
    v-if="showAlert"
    :message="alertMessage"
    :show-help-btn="false"
    alertType="alert-warning"
    closable
    @onClose="showAlert = false"
  />
  <main class="home px-3">
    <!--//////////////////////////////////LOADING//////////////////////////////////-->
    <LoadingOffer v-if="currentStep === RegistrationSteps.LOADING" />
    <!--//////////////////////////////////TERMS & CONTRACT//////////////////////////////////-->
    <RegistrationTermsAndContract
      v-if="currentStep === RegistrationSteps.ACCEPT"
      :marketing-communications-checkbox="marketingCommunicationsCheckbox"
      :user-offer="userOffer"
      from-in-store-flow
      :full-name="fullName"
      @set-doc-modal-content="setDocModalContent"
      @clearSignature="clearSignature"
      @on-change-marketing-communications="onChangeMarketingCommunications"
      @sign-contract="signContract"
    />
    <!--//////////////////////////////////FINISHING PROCESS//////////////////////////////////-->
    <div
      class="container-fluid w-100 h-100 p-0 m-0"
      style="background: #f3f9ed"
      v-if="currentStep === RegistrationSteps.FINISHING_PROCESS"
    >
      <OfferSignLoading
        @animation-finished="offerFetchedAccepted()"
        :is-treatment-one-funnel="false"
      />
    </div>
    <!--//////////////////////////////////NOT APPLICABLE//////////////////////////////////-->
    <RegistrationNotApplicable
      v-if="currentStep === RegistrationSteps.NOT_APPLICABLE"
      in-store
      :person_uuid="person_uuid"
    />

    <Modal :show="showDocsModal">
      <template #header>
        <div class="d-flex flex-row">
          <h5 class="text-black fw-bolder text-start" id="modal-title-header">
            {{ docModalTitle }}
          </h5>
          <span class="ms-auto me-0 right" @click="showDocsModal = false"
            ><i class="fa fa-times" style="font-size: larger !important"></i
          ></span>
        </div>
      </template>
      <template #body class="mt-2">
        <div
          v-if="docsModalContent === RegistrationSteps.ACCEPT"
          ref="termsRef"
          id="modal-body-terms"
          class="content p-2 overflow-auto c1c"
          style="scale: 0.8"
          v-html="termsContent"
        ></div>
        <div
          v-if="docsModalContent === RegistrationSteps.CONTRACT"
          ref="contractRef"
          id="modal-body-contract"
          class="content overflow-auto c1c"
          v-html="contractContent"
        ></div>
      </template>
    </Modal>
  </main>
</template>

<script lang="ts">
import {
  defineComponent,
  defineAsyncComponent,
  onMounted,
  ref,
  watch,
} from "vue";
import { Service } from "@/apps/credit-line/services/network.service";
import { RegistrationSteps } from "@/apps/credit-line/types/registrationSteps";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { useRoute } from "vue-router";
import LoadingOffer from "@/shared-components/consumer/LoadingOffer.vue";
import { useI18n } from "@/apps/core-app-onboarding/composables/useI18n";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import Header from "@/shared-components/consumer/Header.vue";
import Alert from "@/shared-components/common/Alert.vue";
import percentageFormat from "@/apps/credit-line/helpers/percentageFormatter.helper";
import { IOffer } from "@/apps/credit-line/models/IOffer";
import RegistrationTermsAndContract from "@/shared-components/consumer/T&C.vue";
import { buildContractAndTyc } from "@/apps/credit-line/helpers/registrations.helper";
import Modal from "@/apps/credit-line/components/Modal.vue";
import { CL_COUNTRIES } from "@/apps/credit-line/types/cl_countries";
import RegistrationNotApplicable from "@/apps/credit-line/components/Registration/NotApplicable.vue";
import { AndroidInterface } from "@/apps/core-app-onboarding/services/android.service";
import logAnalyticsEvent from "@/apps/core-app-onboarding/services/google-analytics-events.service";
import { LegacyIMCDocService } from "@/apps/credit-line/services/multi-country/legacy-multi-country-doc.service";

declare const Android: AndroidInterface;

export default defineComponent({
  name: "OpenMarketRegistrationView",
  methods: { percentageFormat },
  components: {
    RegistrationNotApplicable,
    // Especial sytax for lazy loaded component
    OfferSignLoading: defineAsyncComponent(
      () =>
        import(
          /* webpackChunkName: "offer-loader" */ "@/shared-components/consumer/OfferSignLoading.vue"
        )
    ),
    Modal,
    RegistrationTermsAndContract,
    Alert,
    Header,
    LoadingOffer,
  },
  setup() {
    const route = useRoute();
    const { t } = useI18n();
    const makeModel = route.query?.make_model as string;
    const manufacturer = route.query?.manufacturer as string;
    const person_uuid = route.query?.person_uuid as string;
    const creditLineApplicationId = route.query?.applicationId as string;
    const ConsumerApiService = new Service();
    const userOffer = ref({} as IOffer);
    const fullName = ref<string>();
    const currentStep = ref<RegistrationSteps>(RegistrationSteps.LOADING);
    const termsRef = ref();
    const contractRef = ref();
    const termsContent = ref<string>();
    const contractContent = ref<string>();
    const showAlert = ref<boolean>(false);
    const alertMessage = ref<string>(t("SELECTANOPTION"));
    const showInfoModal = ref<boolean>(false);
    const marketingCommunicationsCheckbox = ref<boolean>(true);
    // Docs
    const digitalSignature = ref<string>("");
    const docModalTitle = ref<string>();
    const showDocsModal = ref<boolean>(false);
    const docsModalContent = ref<
      RegistrationSteps.ACCEPT | RegistrationSteps.CONTRACT
    >();

    onMounted(async () => {
      logAnalyticsEvent(
        AnalyticEvents.CoreAppOnboarding.OMViewOfferLoading,
        "Credit Line Open Market"
      );
      try {
        const response = await ConsumerApiService.getCreditLineInStoreOffer({
          makeModel,
          manufacturer,
          creditLineApplicationId,
          sdkInt: Number(route.query?.sdkInt) || 30,
        });
        userOffer.value = response.data.offer;
        userOffer.value.country = CL_COUNTRIES.MX; // Temp until we use OM on other countries
        fullName.value = response.data.offer?.fullName || "";
        currentStep.value = RegistrationSteps.ACCEPT;
        const DocService = buildContractAndTyc(
          response.data.offer,
          creditLineApplicationId,
          true //TODO: remove legacy when settings service is implemented in PJO
        ) as LegacyIMCDocService;
        termsContent.value = DocService?.buildTyC();
        contractContent.value = DocService?.buildContract();
      } catch (error) {
        console.log("error fetching", error);
        ErrorHandler.handleNetworkErrorAndLog(error);
        currentStep.value = RegistrationSteps.NOT_APPLICABLE;
      }
      try {
        await ConsumerApiService.triggerCredolabReportsDownload(
          creditLineApplicationId,
          "afterOfferLoaded"
        );
      } catch (error) {
        console.log("error triggering Credolab download", error);
      }
    });

    const switchStep = (selected: RegistrationSteps) => {
      currentStep.value = selected;
      if (currentStep.value === RegistrationSteps.ACCEPT) {
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.ClickOfferSelected,
          "Happy Path Open Market",
          "Offer details"
        );
      }
    };

    watch(showDocsModal, (value) => {
      document.body.style.overflow = value ? "hidden" : "auto";
    });

    watch(currentStep, (val) => {
      if (val === RegistrationSteps.ACCEPT) {
        logAnalyticsEvent(
          AnalyticEvents.CoreAppOnboarding.OMOfferContractShown,
          "Credit Line Open Market"
        );
      }
    });

    const setDocModalContent = (
      step: RegistrationSteps.ACCEPT | RegistrationSteps.CONTRACT
    ) => {
      showDocsModal.value = true;
      docsModalContent.value = step;
      if (step === RegistrationSteps.ACCEPT) {
        docModalTitle.value = t("CL_CONTRACT.SEE_TYC");
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.ClickTyC,
          "Happy Path",
          "Click - T&C triggered"
        );
      }

      if (step === RegistrationSteps.CONTRACT) {
        docModalTitle.value = t("CL_CONTRACT.SEE_CONTRACT");
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.ClickContract,
          "Happy Path",
          "Click - Contract triggered"
        );
      }
    };

    const clearSignature = () => {
      digitalSignature.value = "";
    };

    const onChangeMarketingCommunications = (event: Event) => {
      let target = event?.target as HTMLInputElement;
      marketingCommunicationsCheckbox.value = target.checked;
    };

    const offerFetchedAccepted = () => {
      ConsumerApiService.acceptInStoreOffer({
        creditLineApplicationId,
        manufacturer,
        makeModel,
        digitalSignature: digitalSignature.value as string,
      })
        .then(() => {
          Android.kycFinished(person_uuid);
        })
        .catch(ErrorHandler.handleNetworkErrorAndLog);
    };

    const signContract = (signatureData: {
      isEmpty: boolean;
      data: string | undefined;
    }) => {
      if (signatureData && !signatureData.isEmpty && signatureData.data) {
        alertMessage.value = "";
        showAlert.value = false;
        logAnalyticsEvent(
          AnalyticEvents.CoreAppOnboarding.OMAcceptContract,
          "Credit Line Open Market",
          "Contract signature"
        );
        digitalSignature.value = signatureData.data.replace(
          "data:image/png;base64,",
          ""
        );
        if (userOffer.value?.directLogin) {
          Android.acceptOffer();
        } else {
          currentStep.value = RegistrationSteps.FINISHING_PROCESS;
        }
      } else {
        alertMessage.value = t("PLEASE_SIGN");
        showAlert.value = true;
        window.scrollTo(0, 0);
      }
    };

    return {
      t,
      person_uuid,
      userOffer,
      showInfoModal,
      docModalTitle,
      docsModalContent,
      termsRef,
      contractRef,
      showDocsModal,
      showAlert,
      fullName,
      currentStep,
      creditLineApplicationId,
      RegistrationSteps,
      marketingCommunicationsCheckbox,
      termsContent,
      contractContent,
      switchStep,
      signContract,
      setDocModalContent,
      clearSignature,
      onChangeMarketingCommunications,
      offerFetchedAccepted,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";

main {
  position: relative;
  & * {
    position: relative;
  }
}

#modal-title-header h5 {
  font-style: normal;
  font-family: Nunito;
  font-size: large;
}

#modal-btn-cta {
  border-radius: 10px;
  border: 1px solid var(--main-primary-500, #2c7040);
  background: var(--main-white, #fff);
  color: var(--main-primary-500, #2c7040);
  height: 44px;
  padding: 10px 24px;
  text-align: center;
  font-size: medium;
}

#exp-time {
  color: orange;
}

.offer-bg {
  background-color: #f6fbf9 !important;
  height: 90vh;
}

.light-text {
  color: $pj_color-cool-gray;
}

#modal-body-terms {
  max-height: 65vh !important;
}

#modal-body-contract {
  max-height: 65vh !important;
}

.disabled {
  background-color: $pj-color-gray;
}

.offer-summary {
  display: flex;
  flex-direction: column;
  background: $pj-color-green-hover;
  padding: 10px;
  border-radius: 16px;
}

.loading-bg {
  background: #fff;
}

.gray-text {
  color: $pj-color-gray;
  font-weight: lighter;
}

.btn-disabled {
  background-color: $pj-color-gray;
  cursor: not-allowed;
}

.c1c {
  scale: 0.87 1 !important;
}

.cancel-btn {
  color: $pj-color-green;
  background-color: white;
  border-color: transparent;
}

.clear-btn {
  color: $pj-color-yellow-green;
}

label {
  cursor: pointer;
  color: $pj-color-green;
  font-weight: bold;
  font-size: medium;
}

.checkbox {
  box-shadow: none;
  outline: none;
  border-color: $pj-color-green;

  &:checked {
    background-color: $pj-color-green;
    color: $pj-color-green;
    outline: none;
    border: 0;
    box-shadow: none;
  }
}

#offer-explainer-footer {
  border-radius: 0 0 1rem 1rem;
  background: #f8fbf5;
  position: absolute;
  left: 0;
  right: 0;
  width: 90%;

  & .container {
    & .row {
      margin-top: 0.5rem;
      color: #666;
      font-family: Inter;
      font-size: 0.9375rem;
      font-style: normal;
      font-weight: 500;
      line-height: 1.4375rem;
      letter-spacing: 0.01875rem;
    }
  }
}

#offer-explainer-footer > div:nth-child(n) {
  margin-top: 0.5rem;
  color: #666;
}

#accept-step {
  padding-bottom: 25%;
}
</style>
